import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { HelperTooltip } from 'components/Tooltip';
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
  findPeriodByMob,
  formatCohortPeriodValue,
  getCohortPeriodValue,
} from './calc/cohortSheetPeriodValue';
import {
  getCohortRoas100Counts,
  getCohortRoasAvgWindows,
} from './calc/cohortSheetSummary';
import { formatHillModelSummary, fitHillRoasModel } from './calc/roasSaturation';

function formatRoasPct(value) {
  if (value == null || !Number.isFinite(Number(value)) || Number(value) === 0) {
    return '—';
  }
  return `${Number(value).toFixed(1)}%`;
}

function formatStdevPct(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return '—';
  }
  return `${Number(value).toFixed(1)}%`;
}

function CohortRoasAvgWindowsTable({ cohortSheet }) {
  const windows = getCohortRoasAvgWindows(cohortSheet);
  const maxMob = windows[0]?.avgVectorPct?.length ?? 0;
  if (!windows.length || maxMob === 0) {
    return null;
  }

  return (
    <Box mt={5}>
      <Text fontWeight="semibold" fontSize="sm" mb={1}>
        Trailing cohort ROAS averages
      </Text>
      <Text fontSize="xs" color="gray.500" mb={3}>
        One row per window (Avg last 3 through Avg last 12, capped at available
        cohorts): average of per-MOB sample standard deviations, then the
        average cumulative ROAS vector.
      </Text>
      <Box overflowX="auto">
        <table className="cohort-sheet-table">
          <thead>
            <tr>
              <th className="text-left">Window</th>
              <th className="text-right">Avg stdev</th>
              {Array.from({ length: maxMob }, (_, mob) => (
                <th key={`avg-h-${mob}`} className="text-right">
                  M{mob}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {windows.map((windowStats) => (
              <tr key={`window-${windowStats.avgCohorts}`}>
                <td>{`Avg last ${windowStats.avgCohorts}`}</td>
                <td className="text-right">
                  {formatStdevPct(windowStats.avgStdevPct)}
                </td>
                {windowStats.avgVectorPct.map((value, mob) => (
                  <td
                    key={`avg-${windowStats.avgCohorts}-${mob}`}
                    className="text-right"
                  >
                    {formatRoasPct(value)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}

CohortRoasAvgWindowsTable.propTypes = {
  cohortSheet: PropTypes.object,
};

const MARKOV_FILL_TOOLTIP = (
  <>
    <Text mb={2}>
      <strong>1. Cumulative values:</strong> Period columns must be cumulative within each
      cohort row. If the upload has per-month (incremental) revenue or ROAS, we first add
      each period to the previous to build cumulative totals.
    </Text>
    <Text mb={2}>
      <strong>2. Markov fill (purple italic):</strong> Missing MOB cells use the same method
      as the Sales page. Each row is converted to incremental values (this MOB minus the
      previous MOB). We take older cohorts&apos; incremental[p] / incremental[p−1] ratios.
      For MOB 1–3 we average the last three ratios. From MOB 4, we take the last five,
      drop the highest and lowest when all five exist, and average the rest. Multiply this
      cohort&apos;s previous incremental by that average, then add it back to the previous
      cumulative.
    </Text>
    <Text>
      <strong>3. ROAS vector average:</strong> After the triangle is complete, we average
      cumulative ROAS at each MOB across the last three cohorts. That default curve is shown
      in the editable ROAS vector above and is used for new marketing when spend-based Hill
      saturation cannot be fit.
    </Text>
  </>
);

function CohortSheetTable({
  cohortSheet,
  expanded,
  onToggleExpanded,
  hillModel,
}) {
  const [metricView, setMetricView] = useState('roas');
  const [periodType, setPeriodType] = useState('cumulative');
  const isAlwaysExpanded = onToggleExpanded == null;
  const isExpanded = isAlwaysExpanded ? true : expanded;

  if (!cohortSheet?.cohorts?.length) return null;

  const maxMob =
    cohortSheet.cohorts.reduce(
      (max, row) => Math.max(max, row.periods?.length || 0),
      0
    ) || 0;

  const summaryBase = `${cohortSheet.cohorts.length} cohorts · ${maxMob} MOBs`;
  const roas100Counts = getCohortRoas100Counts(cohortSheet);

  const resolvedHillModel = hillModel ?? fitHillRoasModel(cohortSheet);
  const hillSummary = formatHillModelSummary(resolvedHillModel);

  return (
    <Box mt={4} border="1px solid" borderColor="purple.100" borderRadius="lg" p={4} bg="white">
      <Flex justify="space-between" align="center" gap={2} flexWrap="wrap">
        <Box>
          <Text fontWeight="semibold" fontSize="sm">
            Cohort ROAS data
            {resolvedHillModel?.k ? (
              <Text as="span" fontWeight="normal" color="gray.600">
                {` · avg k $${Math.round(resolvedHillModel.k).toLocaleString()}`}
              </Text>
            ) : null}
          </Text>
          {hillSummary ? (
            <Text fontSize="xs" color="gray.500" mt={0.5}>
              {hillSummary}
            </Text>
          ) : null}
          <Flex fontSize="xs" color="gray.500" align="center" flexWrap="wrap">
            <Text as="span">{summaryBase}</Text>
            {cohortSheet.wasFilled ? (
              <>
                <Text as="span"> · Markov filled</Text>
                <HelperTooltip content={MARKOV_FILL_TOOLTIP} />
              </>
            ) : (
              <Text as="span"> · complete upload</Text>
            )}
          </Flex>
        </Box>
        <Flex align="center" gap={2} flexWrap="wrap">
          <Flex
            fontSize="xs"
            color="gray.600"
            align="center"
            gap={3}
            flexWrap="wrap"
          >
            <Flex align="center" gap={1}>
              <Text as="span">
                {`Actual ≥100% ROAS: ${roas100Counts.actualAtOrAbove100} / ${roas100Counts.totalCohorts}`}
              </Text>
              <HelperTooltip content="Cohorts whose cumulative ROAS from actual (non-predicted) cells already reached 100% or more." />
            </Flex>
            <Flex align="center" gap={1}>
              <Text as="span">
                {`Predicted to ≥100%: ${roas100Counts.predictedToReach100}`}
              </Text>
              <HelperTooltip content="Cohorts not yet at 100% on actual data alone, but reaching 100%+ cumulative ROAS when predicted cells are included." />
            </Flex>
          </Flex>
          {isExpanded ? (
            <Flex gap={1} flexWrap="wrap">
              <Flex gap={1}>
                <Button
                  size="xs"
                  variant={metricView === 'roas' ? 'solid' : 'ghost'}
                  colorScheme="purple"
                  onClick={() => setMetricView('roas')}
                >
                  ROAS
                </Button>
                <Button
                  size="xs"
                  variant={metricView === 'revenue' ? 'solid' : 'ghost'}
                  colorScheme="purple"
                  onClick={() => setMetricView('revenue')}
                >
                  Revenue
                </Button>
              </Flex>
              <Flex gap={1}>
                <Button
                  size="xs"
                  variant={periodType === 'cumulative' ? 'solid' : 'ghost'}
                  colorScheme="purple"
                  onClick={() => setPeriodType('cumulative')}
                >
                  Cumulative
                </Button>
                <Button
                  size="xs"
                  variant={periodType === 'incremental' ? 'solid' : 'ghost'}
                  colorScheme="purple"
                  onClick={() => setPeriodType('incremental')}
                >
                  Incremental
                </Button>
              </Flex>
            </Flex>
          ) : null}
          {!isAlwaysExpanded ? (
            <Button size="xs" variant="outline" onClick={onToggleExpanded}>
              {isExpanded ? 'Hide cohort data' : 'Show cohort data'}
            </Button>
          ) : null}
        </Flex>
      </Flex>
      {isExpanded ? (
        <>
          <Box mt={3} overflowX="auto">
            <table className="cohort-sheet-table">
              <thead>
                <tr>
                  <th className="text-left">Cohort date</th>
                  <th className="text-right">Marketing</th>
                  {Array.from({ length: maxMob }, (_, i) => (
                    <th key={`h-${i}`} className="text-right">
                      M{i}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cohortSheet.cohorts.map((row) => (
                  <tr key={row.cohortIndex}>
                    <td>{row.cohortDate}</td>
                    <td className="text-right">
                      ${Math.round(row.marketingSpend).toLocaleString()}
                    </td>
                    {Array.from({ length: maxMob }, (_, mob) => {
                      const period = findPeriodByMob(row.periods, mob);
                      const prevPeriod =
                        mob > 0 ? findPeriodByMob(row.periods, mob - 1) : null;
                      const value = getCohortPeriodValue(
                        period,
                        prevPeriod,
                        metricView,
                        periodType
                      );
                      const style = period?.isPredicted
                        ? { color: '#805AD5', fontStyle: 'italic' }
                        : undefined;
                      return (
                        <td
                          key={`${row.cohortIndex}-${mob}`}
                          className="text-right"
                          style={style}
                        >
                          {formatCohortPeriodValue(value, metricView)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
          <CohortRoasAvgWindowsTable cohortSheet={cohortSheet} />
        </>
      ) : null}
    </Box>
  );
}

CohortSheetTable.propTypes = {
  cohortSheet: PropTypes.object,
  expanded: PropTypes.bool,
  onToggleExpanded: PropTypes.func,
  hillModel: PropTypes.object,
};

export default CohortSheetTable;
